import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, Modal, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { useTravelI18n } from '../../modules/travel/useTravelI18n';

interface CloneItineraryModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (title: string, startDate: string) => void;
  initialTitle: string;
  loading?: boolean;
}

const CloneItineraryModal: React.FC<CloneItineraryModalProps> = ({
  visible,
  onClose,
  onConfirm,
  initialTitle,
  loading = false,
}) => {
  const { t } = useTravelI18n();
  const [title, setTitle] = useState(initialTitle);
  // Default to today's date in YYYY-MM-DD format
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });

  const handleConfirm = () => {
    if (!title.trim()) return;
    onConfirm(title, startDate);
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.dialog}>
          <Text style={styles.header}>{t('travelPostDetail.cloneModal.title')}</Text>
          
          <View style={styles.field}>
            <Text style={styles.label}>{t('travelPostDetail.cloneModal.labelTitle')}</Text>
            <TextInput
              style={styles.input}
              value={title}
              onChangeText={setTitle}
              placeholder={t('travelPostDetail.cloneModal.placeholderTitle')}
              editable={!loading}
            />
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>{t('travelPostDetail.cloneModal.labelStartDate')}</Text>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: '100%',
                padding: 10,
                fontSize: 16,
                border: '1px solid #ddd',
                borderRadius: 8,
                backgroundColor: loading ? '#f5f5f5' : '#fff',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              disabled={loading}
            />
          </View>

          <View style={styles.actions}>
            <TouchableOpacity 
              style={[styles.button, styles.cancelButton]} 
              onPress={onClose}
              disabled={loading}
            >
              <Text style={styles.cancelText}>{t('travelPostDetail.cloneModal.cancel')}</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[styles.button, styles.confirmButton, loading && styles.disabledButton]} 
              onPress={handleConfirm}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.confirmText}>{t('travelPostDetail.cloneModal.confirm')}</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  dialog: {
    width: '100%',
    maxWidth: 400,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 24,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
      },
      default: {
        elevation: 5,
      },
    }),
  },
  header: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 24,
    color: '#111',
    textAlign: 'center',
  },
  field: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#444',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fff',
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    backgroundColor: '#f5f5f5',
  },
  confirmButton: {
    backgroundColor: '#FB7A4A',
  },
  disabledButton: {
    opacity: 0.7,
  },
  cancelText: {
    color: '#666',
    fontWeight: '600',
    fontSize: 16,
  },
  confirmText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 16,
  },
});

export default CloneItineraryModal;
